import type { Params } from 'react-router-dom';

/**
 * ROBO-932 — the page's own title, declared on its route as `handle: routeTitle('Sites')`.
 *
 * ★ ENGLISH SOURCE STRINGS, NOT `T(...)`. The route tables are module-level consts, evaluated once at
 * import, before the host's locale is known. `RouteTitleAndPageView` translates the tab title at
 * render, and sends the untranslated string to analytics so GA groups a page the same way in every
 * locale.
 *
 * ★ NEVER A RECORD'S DATA. This string is what analytics receives as `page_title`. A site address
 * or a client name belongs in `usePageTitleDetail`, which reaches the browser tab only.
 *
 * A function form exists for the one host route whose page is chosen by a URL segment (the legacy
 * report host, `Reports/legacy/:reportKey?`); it must still return catalogue text, not user data.
 */
export type RouteTitle = string | ((params: Params) => string);

export type RouteTitleHandle = { title: RouteTitle };

export function routeTitle(title: RouteTitle): RouteTitleHandle {
    return { title };
}

/** The minimum of a react-router match this needs — `router.state.matches` and `useMatches()` both fit. */
export type TitledMatch = {
    params: Params;
    route: { handle?: unknown };
};

/** The deepest matched route's title, resolved against its params; undefined when no route declares one. */
export function resolveRouteTitle(matches: readonly TitledMatch[]): string | undefined {
    for (let i = matches.length - 1; i >= 0; i--) {
        const title = (matches[i].route.handle as Partial<RouteTitleHandle> | undefined)?.title;
        if (typeof title === 'string') return title;
        if (typeof title === 'function') return title(matches[i].params);
    }
    return undefined;
}

export const DEFAULT_BRAND_TITLE = 'Photography Portal';

/**
 * The browser tab's text (Herb, 2026-10-29): "Record · Brand" on a record page, "Page · Brand"
 * everywhere else, and the brand alone where no route declares a title. `pageTitle` arrives already
 * translated.
 */
export function buildDocumentTitle({
    pageTitle,
    detail,
    brandName
}: {
    pageTitle?: string;
    detail?: string;
    brandName?: string;
}): string {
    const brand = brandName?.trim() || DEFAULT_BRAND_TITLE;
    const lead = detail?.trim() || pageTitle?.trim();
    return lead ? `${lead} · ${brand}` : brand;
}
